import React, { useState, useEffect, useCallback } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Slettede kunders servicer, ordrer, sykkelkort og godkjente tilbud, som vi må
// oppbevare uendret (bokføringsloven § 13). Kun for regnskap/revisjon —
// hvert åpnet dokument logges på serveren.
const TYPER = [
  { key: "service", label: "Servicer" },
  { key: "gammelService", label: "Gamle servicer" },
  { key: "ordre", label: "Ordrer" },
  { key: "sykkel", label: "Sykkelkort" },
  { key: "tilbud", label: "Godkjente tilbud" },
];

const HENDELSER = {
  "kunde-slettet": "Kunde slettet",
  oppslag: "Oppslag",
  anonymisert: "Anonymisert",
  "innsyn-lenke-sendt": "Innsyn: lenke sendt",
  "innsyn-lastet-ned": "Innsyn: lastet ned",
  "innsyn-sendt-epost": "Innsyn: sendt på e-post",
};

function formatDato(v) {
  if (!v) return "–";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("nb-NO");
}

function formatKr(v) {
  return typeof v === "number" ? `${v.toLocaleString("nb-NO")} kr` : "–";
}

function Salgsbilag({ bilag }) {
  const linjer = [...(bilag.serviceLines || []), ...(bilag.partsLines || []), ...(bilag.fraktLines || [])];
  return (
    <div className="border border-gray-200 rounded-lg p-4 text-sm">
      <div className="flex justify-between mb-2">
        <div>
          <p className="font-semibold">Salgsbilag {bilag.nummer}</p>
          <p className="text-gray-500">{formatDato(bilag.opprettetAt)}</p>
        </div>
        <div className="text-right text-gray-600">
          <p>{bilag.selger?.navn}</p>
          {bilag.selger?.orgnr && <p>Org.nr. {bilag.selger.orgnr}</p>}
        </div>
      </div>
      <p className="mb-2">
        Kunde: {bilag.customerName || "–"} {bilag.customerPhone && `(${bilag.customerPhone})`}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full mb-2">
          <tbody>
            {linjer.map((l, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="py-1">
                  {l.navn || l.beskrivelse || [l.brand, l.product].filter(Boolean).join(" ") || "Linje"}
                  {l.qty > 1 && ` × ${l.qty}`}
                </td>
                <td className="py-1 text-right">{formatKr(l.lineTotal ?? l.pris)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-right space-y-0.5">
        <p>Eks. mva: {formatKr(bilag.sumEksMva)}</p>
        <p>Mva ({bilag.mvaSats ?? "–"} %): {formatKr(bilag.mvaBelop)}</p>
        <p className="font-semibold">Totalt: {formatKr(bilag.totalInkMva)}</p>
      </div>
    </div>
  );
}

function Detalj({ type, id, onLukk }) {
  const [doc, setDoc] = useState(null);
  const [feil, setFeil] = useState("");

  useEffect(() => {
    let avbrutt = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/gdpr/sperrede/${type}/${id}`);
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (!avbrutt) setDoc(data);
      } catch {
        if (!avbrutt) setFeil("Kunne ikke hente posten.");
      }
    })();
    return () => {
      avbrutt = true;
    };
  }, [type, id]);

  return (
    <div className="bg-white rounded-xl shadow p-5 print:shadow-none">
      <div className="flex justify-between items-center mb-4 print:hidden">
        <h2 className="text-lg font-bold">Sperret post</h2>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50">
            Skriv ut
          </button>
          <button onClick={onLukk} className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50">
            Lukk
          </button>
        </div>
      </div>
      {feil && <p className="text-red-600 text-sm">{feil}</p>}
      {!doc && !feil && <p className="text-sm text-gray-500">Henter…</p>}
      {doc && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Sperret {formatDato(doc.gdpr?.sperretDato)} · oppbevares til {formatDato(doc.gdpr?.oppbevaresTil)}
            {doc.gdpr?.anonymisertDato && ` · anonymisert ${formatDato(doc.gdpr.anonymisertDato)}`}
          </p>
          {doc.salgsbilag?.nummer && <Salgsbilag bilag={doc.salgsbilag} />}
          <details open={!doc.salgsbilag?.nummer}>
            <summary className="cursor-pointer text-sm font-medium text-gray-700">Alle opplysninger (uendret)</summary>
            <pre className="mt-2 text-xs bg-gray-50 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(doc, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default function SperredePoster() {
  const [type, setType] = useState("service");
  const [q, setQ] = useState("");
  const [fra, setFra] = useState("");
  const [til, setTil] = useState("");
  const [treff, setTreff] = useState([]);
  const [begrenset, setBegrenset] = useState(false);
  const [laster, setLaster] = useState(false);
  const [feil, setFeil] = useState("");
  const [valgt, setValgt] = useState(null);
  const [logg, setLogg] = useState(null);

  const sok = useCallback(async () => {
    setLaster(true);
    setFeil("");
    try {
      const params = new URLSearchParams({ type });
      if (q.trim()) params.set("q", q.trim());
      if (fra) params.set("fra", fra);
      if (til) params.set("til", til);
      const r = await fetch(`${API_BASE_URL}/gdpr/sperrede?${params}`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      setTreff(data.treff || []);
      setBegrenset(!!data.begrenset);
    } catch {
      setFeil("Kunne ikke hente sperrede poster.");
    } finally {
      setLaster(false);
    }
  }, [type, q, fra, til]);

  useEffect(() => {
    setValgt(null);
    sok();
    // Søk automatisk bare når typen byttes; fritekst/datoer søkes med knappen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  async function visLogg() {
    try {
      const r = await fetch(`${API_BASE_URL}/gdpr/logg`);
      if (!r.ok) throw new Error();
      setLogg(await r.json());
    } catch {
      setLogg([]);
    }
  }

  if (valgt) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <Detalj type={valgt.type} id={valgt.id} onLukk={() => setValgt(null)} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Sperrede poster (regnskap)</h1>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Når en kunde slettes, oppbevares servicer, ordrer, sykkelkort og godkjente tilbud uendret i
          fem år etter regnskapsårets slutt (bokføringsloven § 13), men skjules ellers i systemet.
          Bruk dette oppslaget bare ved behov fra regnskap eller revisjon — hvert åpnet dokument logges.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-wrap gap-1">
          {TYPER.map((t) => (
            <button
              key={t.key}
              onClick={() => setType(t.key)}
              className={`px-3 py-1.5 rounded text-sm ${type === t.key ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sok();
          }}
          className="flex flex-wrap gap-2 items-end"
        >
          <label className="text-xs text-gray-600">
            Søk
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Referanse, bilagsnr., navn …"
              className="block border border-gray-300 rounded px-2 py-1.5 text-sm w-56"
            />
          </label>
          {type !== "ordre" && type !== "gammelService" && (
            <>
              <label className="text-xs text-gray-600">
                Fra
                <input type="date" value={fra} onChange={(e) => setFra(e.target.value)} className="block border border-gray-300 rounded px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs text-gray-600">
                Til
                <input type="date" value={til} onChange={(e) => setTil(e.target.value)} className="block border border-gray-300 rounded px-2 py-1.5 text-sm" />
              </label>
            </>
          )}
          <button type="submit" className="px-4 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700">
            Søk
          </button>
        </form>
      </div>

      {feil && <p className="text-red-600 text-sm">{feil}</p>}

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2">Ref.</th>
              <th className="px-3 py-2">Bilag</th>
              <th className="px-3 py-2">Dato</th>
              <th className="px-3 py-2">Kunde</th>
              <th className="px-3 py-2">Beskrivelse</th>
              <th className="px-3 py-2 text-right">Beløp</th>
              <th className="px-3 py-2">Oppbevares til</th>
            </tr>
          </thead>
          <tbody>
            {laster && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-gray-500">Henter…</td>
              </tr>
            )}
            {!laster && treff.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-gray-500">Ingen sperrede poster.</td>
              </tr>
            )}
            {!laster &&
              treff.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setValgt({ type, id: t.id })}
                  className="border-t border-gray-100 hover:bg-indigo-50 cursor-pointer"
                >
                  <td className="px-3 py-2 font-mono">{t.ref || "–"}</td>
                  <td className="px-3 py-2">{t.bilag || "–"}</td>
                  <td className="px-3 py-2">{formatDato(t.dato)}</td>
                  <td className="px-3 py-2">{t.anonymisert ? <em className="text-gray-400">anonymisert</em> : t.kunde || "–"}</td>
                  <td className="px-3 py-2">{t.beskrivelse || "–"}</td>
                  <td className="px-3 py-2 text-right">{formatKr(t.belop)}</td>
                  <td className="px-3 py-2">{formatDato(t.oppbevaresTil)}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {begrenset && <p className="px-3 py-2 text-xs text-gray-500">Viser de 200 nyeste treffene. Snevre inn søket for å se flere.</p>}
      </div>

      <div>
        {logg === null ? (
          <button onClick={visLogg} className="text-sm text-indigo-600 hover:underline">
            Vis GDPR-logg
          </button>
        ) : (
          <div className="bg-white rounded-xl shadow p-4">
            <h2 className="font-semibold mb-2">GDPR-logg (siste 200)</h2>
            {logg.length === 0 ? (
              <p className="text-sm text-gray-500">Ingen hendelser.</p>
            ) : (
              <ul className="text-sm space-y-1">
                {logg.map((l) => (
                  <li key={l._id}>
                    {new Date(l.tidspunkt).toLocaleString("nb-NO")} · {HENDELSER[l.hendelse] || l.hendelse}
                    {l.modell && ` · ${l.modell}`}
                    {l.kundenummer != null && ` · kundenr. ${l.kundenummer}`}
                    {l.utfortAv && ` · ${l.utfortAv}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
